import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import moment from "moment";
import Loader from "../../../../../../core/common/loader";
import { notify } from "../../../../../../services/toast.config";
import {
  appplyLeave,
  getLeaveTypes,
  getUserLeaveSummary,
  updateLeaveStatus,
} from "../../../../../../services/attendanceService";
import LeaveHeader from "../components/LeaveHeader";
import LeaveStatsCards from "../components/LeaveStatsCards";
import LeaveHistorySection from "../components/LeaveHistorySection";
import LeaveFooter from "../components/LeaveFooter";
import AddLeaveModal from "../modals/AddLeaveModal";
import EditLeaveModal from "../modals/EditLeaveModal";
import DeleteLeaveModal from "../modals/DeleteLeaveModal";
import { formatLeaveHistoryForTable } from "../utils/leaveHelpers";

const LeaveEmployee = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [leaveType, setLeaveType] = useState<any>([]);
  const [userLeaveSummary, setUserLeaveSummary] = useState<any>();
  const [allLeaveHistory, setAllLeaveHistory] = useState<any>([]);
  const [dateRange, setDateRange] = useState<{
    start: moment.Moment | null;
    end: moment.Moment | null;
  }>({
    start: null,
    end: null,
  });
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [leaveTypeFilter, setLeaveTypeFilter] = useState<string>("");
  const [modalVisibility, setModalVisibility] = useState(false);
  const [editModalVisibility, setEditModalVisibility] = useState(false);
  const [deleteModalVisibility, setDeleteModalVisibility] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<any>(null);
  const [leaveToDelete, setLeaveToDelete] = useState<any>(null);

  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchLeaveTypes = useCallback(async () => {
    try {
      const res = await getLeaveTypes();
      const mappedLeaveTypes = res.data.data.leaveTypes.map((type: any) => ({
        value: type.id,
        label: type.name,
        isMonthlyAccrual: type.isMonthlyAccrual,
        monthlyLimit: type.monthlyLimit,
        monthlyAllocation: type.monthlyAllocation,
        isActive: type.isActive,
        balance: type.balance,
      }));
      setLeaveType(mappedLeaveTypes.filter((t: any) => t.isActive));
    } catch (error) {
      notify.error("Failed to fetch leave types");
    }
  }, []);

  const fetchLeaveSummary = useCallback(async (start: moment.Moment | null, end: moment.Moment | null) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setAllLeaveHistory([]);
    setUserLeaveSummary(undefined);
    setTableLoading(true);

    try {
      const params: any = {
        startDate: start ? start.format("YYYY-MM-DD") : null,
        endDate: end ? end.format("YYYY-MM-DD") : null,
      };
      const res = await getUserLeaveSummary(params);
      const data = res.data.data;
      setUserLeaveSummary(data);
      setAllLeaveHistory(formatLeaveHistoryForTable(data.leaveHistory || []));
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        notify.error("Failed to fetch leave summary");
      }
    } finally {
      setIsLoading(false);
      setTableLoading(false);
    }
  }, []);

  const filteredLeaveHistory = useMemo(() => {
    let filtered = [...allLeaveHistory];
    if (statusFilter) {
      filtered = filtered.filter(
        (leave) => leave.Status.toLowerCase() === statusFilter.toLowerCase()
      );
    }
    if (leaveTypeFilter) {
      filtered = filtered.filter(
        (leave) => leave.leaveTypeId === parseInt(leaveTypeFilter)
      );
    }
    return filtered;
  }, [allLeaveHistory, statusFilter, leaveTypeFilter]);

  const handleDateChange = useCallback((start: moment.Moment, end: moment.Moment) => {
    setDateRange({ start, end });
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      fetchLeaveSummary(start, end);
    }, 500);
  }, [fetchLeaveSummary]);

  const handleResetDateRange = useCallback(() => {
    const start = null;
    const end = null;
    setDateRange({ start, end });
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    fetchLeaveSummary(start, end);
  }, [fetchLeaveSummary]);

  const handleLeaveTypeChange = useCallback((leaveTypeId: string) => {
    setLeaveTypeFilter(leaveTypeId);
  }, []);

  const handleStatusChange = useCallback((status: string) => {
    setStatusFilter(status);
  }, []);

  const handleClearFilters = useCallback(() => {
    setStatusFilter("");
    setLeaveTypeFilter("");
  }, []);

  const handleSubmitLeave = useCallback(async (values: any) => {
    try {
      const res = await appplyLeave({
        leaveTypeId: values.leaveType.value || values.leaveType,
        startDate: values.fromDate,
        endDate: values.toDate,
        days: values.days,
        reason: values.reason,
        leaveRequestId: selectedLeave?.id,
        documents: values.documents,
        startDayPart: values.startDayPart,
        endDayPart: values.endDayPart,
      });
      if (res.status === 201 || res.status === 200) {
        notify.success(res.data.message.message || "Leave applied successfully!");
        setModalVisibility(false);
        setEditModalVisibility(false);
        setSelectedLeave(null);
        await fetchLeaveSummary(dateRange.start, dateRange.end);
      }
    } catch (error: any) {
      const errorMessage = error.data?.message?.message || "Failed to apply leave";
      notify.error(errorMessage);
      throw error;
    }
  }, [selectedLeave, dateRange, fetchLeaveSummary]);

  const confirmDelete = useCallback(async () => {
    try {
      if (!leaveToDelete) return;
      const res = await updateLeaveStatus(leaveToDelete.id, "cancelled");
      if (res.status === 200 || res.status === 201) {
        notify.info("Leave cancelled successfully");
        await fetchLeaveSummary(dateRange.start, dateRange.end);
      }
    } catch (error) {
      notify.error("Failed to cancel leave");
    } finally {
      setDeleteModalVisibility(false);
      setLeaveToDelete(null);
    }
  }, [leaveToDelete, dateRange, fetchLeaveSummary]);

  const handleEditClick = useCallback((record: any) => {
    setSelectedLeave(record);
    setEditModalVisibility(true);
  }, []);

  const handleDeleteClick = useCallback((record: any) => {
    setLeaveToDelete(record);
    setDeleteModalVisibility(true);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    fetchLeaveTypes();
    fetchLeaveSummary(dateRange.start, dateRange.end);
  }, [fetchLeaveTypes, fetchLeaveSummary]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const isLoadingData = isLoading || tableLoading;

  return (
    <>
      <div className="page-wrapper">
        {isLoadingData ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "100vh" }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading data...</span>
            </div>
          </div>
        ) : (
          <div className="content">
            <LeaveHeader onAddLeave={() => setModalVisibility(true)} />
            <LeaveStatsCards leaveBalances={userLeaveSummary?.leaveBalances || []} />
            <LeaveHistorySection
              leaveHistory={filteredLeaveHistory}
              leaveTypes={leaveType}
              dateRange={dateRange}
              statusFilter={statusFilter}
              leaveTypeFilter={leaveTypeFilter}
              onDateChange={handleDateChange}
              onResetDateRange={handleResetDateRange}
              onLeaveTypeChange={handleLeaveTypeChange}
              onStatusChange={handleStatusChange}
              onClearFilters={handleClearFilters}
              onEditClick={handleEditClick}
              onDeleteClick={handleDeleteClick}
            />
          </div>
        )}
        <LeaveFooter />
      </div>

      <AddLeaveModal
        visible={modalVisibility}
        leaveTypes={leaveType}
        leaveBalances={userLeaveSummary?.leaveBalances || []}
        onClose={() => setModalVisibility(false)}
        onSubmit={handleSubmitLeave}
      />

      <EditLeaveModal
        visible={editModalVisibility}
        selectedLeave={selectedLeave}
        leaveTypes={leaveType}
        leaveBalances={userLeaveSummary?.leaveBalances || []}
        onClose={() => {
          setEditModalVisibility(false);
          setSelectedLeave(null);
        }}
        onSubmit={handleSubmitLeave}
      />

      <DeleteLeaveModal
        visible={deleteModalVisibility}
        leaveToDelete={leaveToDelete}
        onClose={() => setDeleteModalVisibility(false)}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default LeaveEmployee;
